import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PiStarFill, PiStarLight } from "react-icons/pi";
import client from "../../api/client";

const FEATURE_LABELS = {
  job_board: "the Job Board",
  cv_review: "CV Review",
};

export default function FeatureFeedbackBox({ feature }) {
  const [existing, setExisting] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    client
      .get(`/me/feature-feedback/${feature}`)
      .then((res) => {
        if (cancelled) return;
        const feedback = res.data?.feedback;
        if (feedback) {
          setExisting(feedback);
          setRating(feedback.rating);
          setComment(feedback.comment || "");
        }
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [feature]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Pick a rating first."); return; }

    setSaving(true);
    try {
      const res = await client.post("/me/feature-feedback", { feature, rating, comment: comment.trim() || undefined });
      setExisting(res.data.feedback);
      setEditing(false);
      toast.success("Thanks for your feedback!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save your feedback.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const showForm = !existing || editing;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <h2 className="text-base font-semibold text-gray-800 mb-1">
        How's {FEATURE_LABELS[feature] || "this feature"} working for you?
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Your feedback helps us decide what to fix or build next.
      </p>

      {showForm ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => {
              const filled = n <= (hoverRating || rating);
              const Icon = filled ? PiStarFill : PiStarLight;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="text-2xl text-[#F05A19] transition-transform hover:scale-110"
                  aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
                >
                  <Icon />
                </button>
              );
            })}
          </div>

          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Anything you'd like to tell us? (optional)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40 resize-none"
          />

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="bg-[#F05A19] hover:bg-[#d14d14] text-white rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60"
            >
              {saving ? "Saving…" : existing ? "Update Feedback" : "Submit Feedback"}
            </button>
            {existing && editing && (
              <button
                type="button"
                onClick={() => { setEditing(false); setRating(existing.rating); setComment(existing.comment || ""); }}
                className="border border-gray-300 rounded-lg px-5 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      ) : (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1 text-[#F05A19]">
            {[1, 2, 3, 4, 5].map((n) =>
              n <= existing.rating ? <PiStarFill key={n} /> : <PiStarLight key={n} />
            )}
          </div>
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-[#F05A19] font-medium hover:underline"
          >
            Edit your feedback
          </button>
        </div>
      )}
    </div>
  );
}
